import { PAYMENT_TYPE } from '@/shared/web/type';
import dayjs from 'dayjs';
import { date } from 'quasar';
import { Operations, Status } from '@/app/graphql/payment/sdk.payment';
import {
  PayControlKeyAvailabilityFragment,
  SignRight,
  SignScheme,
  SignType,
} from '@/app/graphql/access-control/sdk.access-control';
import { TimeRuleDto } from '@/app/graphql/rule-engine/sdk.rule-engine';
import formatDate = date.formatDate;
import type { PaginatedSalaryDetails } from '@/shared/services/import-payment';
import { HISTORY_FILTER_STATUS } from '@/app/stores/history-transaction/types';

export interface ParamsPayment {
  createdBy: string;
  updatedBy: string;
  valueDate: string;
  userReferenceId: number;
  statusCode: Status;
  operationCode: Operations;
  signRight?: SignRight;
  signScheme?: SignScheme;
  signType?: SignType;
  keyAvailability?: PayControlKeyAvailabilityFragment;
  timeRule: TimeRuleDto;
}

export interface ParamsPaymentSubscription {
  createdBy: string;
  updatedBy: string;
  statusCode: Status;
  userReferenceId: number;
  signRight?: SignRight;
}

export function generatePaymentType(operationCode: Operations) {
  if (!operationCode) {
    return;
  }

  const typeCode = PAYMENT_TYPE[operationCode];
  return typeCode ? `PAYMENT.TYPE.${typeCode}` : 'PAYMENT.TYPE.NO_TITLE';
}

export function generatePaymentStatusLabel(paramsPayment: ParamsPayment | ParamsPaymentSubscription) {
  if (!paramsPayment || !paramsPayment.statusCode) return;
  const isCreateYou = +paramsPayment.createdBy === paramsPayment.userReferenceId;
  const hasSecondSignRight = paramsPayment.signRight === SignRight.Second;
  const hasFirstSignRight = paramsPayment.signRight === SignRight.First;

  switch (paramsPayment.statusCode) {
    case Status.Draft:
      return {
        label: 'HISTORY.STATUS.DRAFT',
        classList: 'ob-text-muted',
      };
    case Status.PartiallyConfirmed:
      return {
        label: hasSecondSignRight ? 'HISTORY.STATUS.PARTIALLY_CONFIRMED_SUFFIX' : 'HISTORY.STATUS.PARTIALLY_CONFIRMED',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.PartialConfirmationCompleted:
      return {
        label: hasSecondSignRight
          ? 'HISTORY.STATUS.PARTIAL_CONFIRMATION_COMPLETED_SUFFIX'
          : 'HISTORY.STATUS.PARTIAL_CONFIRMATION_COMPLETED',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.OnVerification:
      return {
        label: hasFirstSignRight ? 'HISTORY.STATUS.ON_VERIFICATION_SUFFIX' : 'HISTORY.STATUS.ON_VERIFICATION',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.OnVerificationCompleted:
      return {
        label: hasFirstSignRight
          ? 'HISTORY.STATUS.ON_VERIFICATION_COMPLETED_SUFFIX'
          : 'HISTORY.STATUS.ON_VERIFICATION_COMPLETED',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.FirstSignInPayControlComplete:
      return {
        label: hasFirstSignRight
          ? 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL_COMPLETE_SUFFIX'
          : 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL_COMPLETE',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.FirstSignInPayControl:
      return {
        label: 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL',
        classList: 'status-warning',
      };
    case Status.TimeoutCanceledSignTwo:
      return {
        label: 'HISTORY.STATUS.TIMEOUT_CANCELED_SIGN_TWO',
        classList: 'status-danger',
      };
    case Status.TimeoutCanceledSignOne:
      return {
        label: 'HISTORY.STATUS.TIMEOUT_CANCELED_SIGN_ONE',
        classList: 'status-danger',
      };
    case Status.SecondSignInPayControlComplete:
      return {
        label: hasSecondSignRight
          ? 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL_COMPLETE_SUFFIX'
          : 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL_COMPLETE',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.SecondSignInPayControl:
      return {
        label: 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL',
        classList: 'status-warning',
      };
    case Status.CanceledSignOne:
      return {
        label: 'HISTORY.STATUS.CANCELED_SIGN_ONE',
        classList: 'status-danger',
      };
    case Status.CanceledSignTwo:
      return {
        label: 'HISTORY.STATUS.CANCELED_SIGN_TWO',
        classList: 'status-danger',
      };
    case Status.ForEditing:
      return {
        label: isCreateYou ? 'HISTORY.STATUS.REJECTED_SUFFIX' : 'HISTORY.STATUS.FOR_EDITING',
        classList: isCreateYou ? 'status-info' : 'status-warning',
      };
    case Status.OnConfirmationCompleted:
      return {
        label: 'HISTORY.STATUS.CONFIRMED',
        classList: 'status-warning',
      };
    case Status.Revoke:
      return {
        label: 'HISTORY.STATUS.REVOKE',
        classList: 'status-danger',
      };
    case Status.WaitResponseFromBank:
      return {
        label: 'HISTORY.STATUS.WAIT_RESPONSE_FROM_BANK',
        classList: 'status-warning',
      };
    case Status.TimeIsOut:
      return {
        label: 'HISTORY.STATUS.TIME_IS_OUT.TITLE',
        classList: 'status-danger',
      };
    case Status.PaymentRequestToBank:
      return {
        label: 'HISTORY.STATUS.PAYMENT_REQUEST_TO_BANK',
        classList: 'status-warning',
      };
    case Status.Kvit:
      return {
        label: 'HISTORY.STATUS.KVIT',
        classList: 'status-warning',
      };
    case Status.Success:
      return {
        label: 'HISTORY.STATUS.SUCCESS',
        classList: 'status-success',
      };
    case Status.Error:
      return {
        label: 'HISTORY.STATUS.ERROR',
        classList: 'status-danger',
      };
    default:
      return {
        label: 'HISTORY.STATUS.UNKNOWN',
        classList: 'status-danger',
      };
  }
}
export function convertToDayHourFormat(date: string) {
  if (!date) return undefined;

  const formattedDate = formatDate(date, 'DD.MM.YYYY HH:mm');
  return { time: formattedDate };
}
export function mapDateTime(date: string, withTime: boolean = true): any {
  const isToday = dayjs().isSame(dayjs(date), 'day');
  const isYesterday = dayjs().subtract(1, 'day').isSame(dayjs(date), 'day');
  const time = dayjs(date).format('HH:mm');
  if (isToday) {
    return withTime
      ? { text: 'STATEMENT.TRANSACTION.DATE.TODAY_TIME', time }
      : { text: 'STATEMENT.TRANSACTION.DATE.TODAY' };
  } else if (isYesterday) {
    return withTime
      ? { text: 'STATEMENT.TRANSACTION.DATE.YESTERDAY_TIME', time }
      : { text: 'STATEMENT.TRANSACTION.DATE.YESTERDAY' };
  }

  return convertToDayHourFormat(date);
}
export function getPaymentImg(paymentType: Operations) {
  switch (paymentType) {
    case Operations.Clearing:
    case Operations.Gross:
      return 'som';
    case Operations.Swift:
      return 'swift';
    case Operations.IntrabankTransferOwn:
      return 'intrabank-transfer-own';
    case Operations.IntrabankTransferForeign:
      return 'intrabank-transfer-foreign';
    case Operations.TransferByInvoice:
      return 'transfer-by-invoice';
    case Operations.Rko_2BusinessCardOwn:
      return 'rko-2-business-card';
    case Operations.Rko_2BusinessCardForeign:
    case Operations.Rko_2IndividualCardOwn:
    case Operations.Rko_2IndividualCardForeign:
      return 'rko-2-individual-card';
    case Operations.BusinessCard_2RkoOwn:
    case Operations.BusinessCard_2RkoForeign:
      return 'business-card-2-rko';
    case Operations.Conversion:
      return 'conversion';
    case Operations.BudgetPayment:
      return 'budget-payment';
    case Operations.Refund:
      return 'qr-return';
    case Operations.Qr:
      return 'qr-payment';
    default:
      return '';
  }
}

export enum TYPE_EXPORT_FILE {
  DOWNLOAD = 'DOWNLOAD',
  OPEN = 'OPEN',
  PRINT = 'PRINT',
}

export const INITIAL_PAGEABLE_SALARY_STATEMENT = { amountElements: 125, currentPage: 1 };
export const INITIAL_PAGINATION_SALARY_FILE_DETAIL = { amountElements: 225, currentPage: 1 };
export const INITIAL_PAGINATION_SALARY_STATEMENT_DETAIL = { amountElements: 225, currentPage: 1 };
export const INITIAL_SALARY_STATEMENT = {
  amountPages: 0,
  amountElements: 0,
  list: [],
};
export const INITIAL_FILTER_SALARY_STATEMENT = {
  startDate: null,
  endDate: null,
  status: HISTORY_FILTER_STATUS.ALL,
};

export const INITIAL_SALARY_FILE_DETAIL: PaginatedSalaryDetails = {
  amountPages: 0,
  amountElements: 0,
  currentPage: 0,
  list: [],
};
export function generateSalaryStatementStatusLabel(
  statusCode: Status,
  signRight: SignRight,
  isFullComplete?: boolean | null
) {
  if (!statusCode) return;

  const hasSecondSignRight = signRight === SignRight.Second;
  const hasFirstSignRight = signRight === SignRight.First;

  switch (statusCode) {
    case Status.Draft:
      return {
        label: 'HISTORY.LISTS.DRAFT_SALARY',
        classList: 'ob-text-muted',
      };
    case Status.PartiallyConfirmed:
      return {
        label: hasSecondSignRight ? 'HISTORY.STATUS.PARTIALLY_CONFIRMED_SUFFIX' : 'HISTORY.STATUS.PARTIALLY_CONFIRMED',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.PartialConfirmationCompleted:
      return {
        label: hasSecondSignRight
          ? 'HISTORY.STATUS.PARTIAL_CONFIRMATION_COMPLETED_SUFFIX'
          : 'HISTORY.STATUS.PARTIAL_CONFIRMATION_COMPLETED',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.OnVerification:
      return {
        label: hasFirstSignRight ? 'HISTORY.STATUS.ON_VERIFICATION_SUFFIX' : 'HISTORY.STATUS.ON_VERIFICATION',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.OnVerificationCompleted:
      return {
        label: hasFirstSignRight
          ? 'HISTORY.STATUS.ON_VERIFICATION_COMPLETED_SUFFIX'
          : 'HISTORY.STATUS.ON_VERIFICATION_COMPLETED',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.FirstSignInPayControlComplete:
      return {
        label: hasFirstSignRight
          ? 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL_COMPLETE_SUFFIX'
          : 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL_COMPLETE',
        classList: hasFirstSignRight ? 'status-info' : 'status-warning',
      };
    case Status.FirstSignInPayControl:
      return {
        label: 'HISTORY.STATUS.FIRST_SIGN_IN_PAY_CONTROL',
        classList: 'status-warning',
      };
    case Status.TimeoutCanceledSignTwo:
      return {
        label: 'HISTORY.STATUS.TIMEOUT_CANCELED_SIGN_TWO',
        classList: 'status-danger',
      };
    case Status.TimeoutCanceledSignOne:
      return {
        label: 'HISTORY.STATUS.TIMEOUT_CANCELED_SIGN_ONE',
        classList: 'status-danger',
      };
    case Status.SecondSignInPayControlComplete:
      return {
        label: hasSecondSignRight
          ? 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL_COMPLETE_SUFFIX'
          : 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL_COMPLETE',
        classList: hasSecondSignRight ? 'status-info' : 'status-warning',
      };
    case Status.SecondSignInPayControl:
      return {
        label: 'HISTORY.STATUS.SECOND_SIGN_IN_PAY_CONTROL',
        classList: 'status-warning',
      };
    case Status.CanceledSignOne:
      return {
        label: 'HISTORY.STATUS.CANCELED_SIGN_ONE',
        classList: 'status-danger',
      };
    case Status.CanceledSignTwo:
      return {
        label: 'HISTORY.STATUS.CANCELED_SIGN_TWO',
        classList: 'status-danger',
      };
    case Status.OnConfirmationCompleted:
      return {
        label: 'HISTORY.STATUS.CONFIRMED',
        classList: 'status-warning',
      };
    case Status.WaitResponseFromBank:
      return {
        label: 'HISTORY.STATUS.WAIT_RESPONSE_FROM_BANK',
        classList: 'status-warning',
      };
    case Status.TimeIsOut:
      return {
        label: 'HISTORY.STATUS.TIME_IS_OUT.TITLE',
        classList: 'status-danger',
      };
    case Status.PaymentRequestToBank:
      return {
        label: 'HISTORY.STATUS.PAYMENT_REQUEST_TO_BANK',
        classList: 'status-warning',
      };
    case Status.Success:
      return {
        label: isFullComplete ? 'HISTORY.STATUS.SUCCESS' : 'HISTORY.STATUS.PARTIALLY_EXECUTED',
        classList: isFullComplete ? 'status-success' : 'status-warning',
      };
    case Status.Error:
      return {
        label: 'HISTORY.STATUS.ERROR',
        classList: 'status-danger',
      };
    default:
      return {
        label: 'HISTORY.STATUS.UNKNOWN',
        classList: 'status-danger',
      };
  }
}
